import type { DeviceHealthSummary } from '@platform/shared-types';

export interface BuildHealthRow {
  version: string; devices: number; sessions: number;
  crashFreePct: number | null; errorsPerSession: number | null; payAnomalies: number | null;
  verdict: 'healthy' | 'watch' | 'worse' | 'no-data';
  /** Mockup view ③'s one-line comparative sentence, backed by the same numbers as the badge. `null` for the incumbent itself — there is nothing to compare it against. */
  summary: string | null;
}

const MIN_SESSIONS = 20;

export function liveVersions(devices: Array<{ appVersion?: string }>, max = 4): Array<{ version: string; devices: number }> {
  const n = new Map<string, number>();
  for (const d of devices) {
    const v = (d.appVersion || '').trim();
    if (!v || v === 'dev' || v === 'unknown') continue;
    n.set(v, (n.get(v) || 0) + 1);
  }
  return [...n.entries()].map(([version, c]) => ({ version, devices: c })).sort((a, b) => b.devices - a.devices).slice(0, max);
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

/** Spec §9 placement 3: a promote decision gets a health signal — each build against the incumbent (most devices). */
export function toBuildRows(
  live: Array<{ version: string; devices: number }>,
  summaries: Record<string, Pick<DeviceHealthSummary, 'sessions' | 'crashFreePct' | 'errorsPerSession' | 'payAnomalies'> | null>,
): BuildHealthRow[] {
  const base = live[0] ? summaries[live[0].version] : null;
  return live.map(({ version, devices }) => {
    const s = summaries[version];
    const row = {
      version, devices,
      sessions: s?.sessions ?? 0,
      crashFreePct: s?.crashFreePct ?? null,
      errorsPerSession: s?.errorsPerSession ?? null,
      payAnomalies: s?.payAnomalies ?? null,
    };
    if (!s || s.sessions < MIN_SESSIONS || s.crashFreePct === null) {
      return { ...row, verdict: 'no-data' as const, summary: `Not enough data yet (${plural(row.sessions, 'session')})` };
    }
    if (!base || base === s || base.crashFreePct === null) {
      // The incumbent (or the only build reporting) — nothing to compare it against.
      return { ...row, verdict: 'healthy' as const, summary: null };
    }
    const cfOk = s.crashFreePct >= base.crashFreePct - 0.5;
    const epsOk = s.errorsPerSession === null || base.errorsPerSession === null || s.errorsPerSession <= base.errorsPerSession * 1.25;
    const comparison = `Crash-free ${s.crashFreePct.toFixed(1)}% vs ${base.crashFreePct.toFixed(1)}% on the current build (${plural(s.sessions, 'session')})`;
    if (cfOk && epsOk) return { ...row, verdict: 'healthy' as const, summary: `${comparison} — safe to promote` };
    if (s.crashFreePct >= base.crashFreePct - 2) return { ...row, verdict: 'watch' as const, summary: `${comparison} — hold the promote` };
    return { ...row, verdict: 'worse' as const, summary: `${comparison} — hold the promote` };
  });
}
